import type { User } from '@/features/user/types/user';
import { apiClient, type ApiClient } from '@/services/apiClient';
// Credentials 定义在 api/auth.ts（@/types/auth 从未导出过该类型）；import type 编译期擦除，无运行时引入
import type { Credentials } from '@/api/auth';
import type { ApiResponse } from '@/types/apiResponse';
import type { MenuItem, BatchLogoutResponse } from '@/types/menu';
import { API_BASE } from '@/constants/api';

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number; // seconds
}

/**
 * `POST /system/auth/token/refresh` 信封里 data 部分的形状（snake_case——
 * 后端 JWTOutSchema.model_dump() 不带 by_alias）。注意本文件的 apiClient 是
 * 独立 axios 实例、不做信封解包，post 返回完整 { code, msg, data } body。
 */
export interface TokenRefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface LoginResult {
  success: boolean;
  access_token: string;
}

// 成功响应类型（200状态码）
type AuthSuccessResponse = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
};

export class AuthService {
  private accessTokenKey = 'ACCESS_TOKEN';
  private refreshTokenKey = 'REFRESH_TOKEN';
  constructor(private api: ApiClient) {}

  async initialize(): Promise<void> {
    console.log('🔧 Auth service initialized - JWT middleware will handle token injection');
  }

  async hasValidToken(): Promise<boolean> {
    const accessToken = await this.getAccessToken();
    return !!accessToken;
  }

  async login(credentials: Credentials): Promise<LoginResult> {
    // 仅非 prod 打印（含明文密码，绝不能进生产 console）
    if (import.meta.env.MODE !== 'prod') console.log('Login credentials:', credentials);
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    
    const response = await this.api.post<AuthSuccessResponse, FormData>(
        `${API_BASE}/system/auth/login`,
        formData,
        {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }
    );

    const access_token = response.access_token;
    const refresh_token = response.refresh_token;
    await this.storeTokens(access_token, refresh_token);
    this.api.setHeader('Authorization', `Bearer ${access_token}`);
    // 仅非 prod 打印（含明文 access_token，绝不能进生产 console）
    if (import.meta.env.MODE !== 'prod') console.log('✅ Tokens stored and Authorization header set:', access_token);
    
    const storedToken = await this.getAccessToken();
    if (storedToken !== access_token) {
      console.warn('⚠️ Token storage verification failed!');
    } else {
      console.log('🔒 Token storage verified.');
    }
    
    console.log('✅ Tokens stored - middleware will automatically add to future requests');
    return { success: true, access_token };
  }

  async logout(): Promise<void> {
    const accessToken = await this.getAccessToken();
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    console.log('🧹 Tokens cleared from storage - middleware will stop adding them to requests');
    


    try {
      if (accessToken) {
        console.log("user defined logout")
        await this.api.post(`${API_BASE}/system/auth/logout`, 
          { token: accessToken }, 
          {
            headers: { 
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }
    } catch (error) {
      console.log('Logout API call failed, but local logout completed:', error);
    }
  }

  async register(userData: { firstName: string; lastName: string; username: string; password: string; [key: string]: any }): Promise<User> {
    const { firstName, lastName, ...rest } = userData;
    const requestData = {
      ...rest,
      full_name: `${firstName} ${lastName}`.trim(),
      department_id: '7333087067523518465'
    };

    console.log('Registration request data:', requestData);
    const response = await this.api.post<User, typeof requestData>(`${API_BASE}/system/user/register`, requestData, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response;
  }

  async createUser(userData: { email: string; password: string; role?: string; [key: string]: any }): Promise<User> {
    const response = await this.api.post<User, typeof userData>(`${API_BASE}/user/create`, userData, {
      headers: { 
        'Content-Type': 'application/json',
      },
    });
    return response;
  }

  async resetPassword(userData: {username: string, mobile: string, newPassword: string}): Promise<User> {
    const requestData = {
      username: userData.username,
      mobile: userData.mobile,
      new_password: userData.newPassword
    };

    const user = await this.api.post<User, typeof requestData>(
      `${API_BASE}/system/user/forget/password`,
      requestData,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );

    return user;
  }

  async resetForgottenPassword(userData: {username: string, mobile: string, newPassword: string}): Promise<User> {
    const requestData = {
      username: userData.username,
      mobile: userData.mobile,
      new_password: userData.newPassword
    };

    const user = await this.api.post<User, typeof requestData>(
      `${API_BASE}/system/user/forget/password`,
      requestData,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );

    return user;
  }

  async fetchUserProfile(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>(
      `${API_BASE}/system/user/current/info` 
    );
    console.log("USER",response.data)
    return response.data;
  }

  async updateUserProfile(userData: { firstName?: string; lastName?: string; email?: string; [key: string]: any }): Promise<User> {
    const response = await this.api.put<User, typeof userData>(`${API_BASE}/system/user/current/update`, userData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  }

  async updateUserAvatar(formData: FormData): Promise<User> {
    const response = await this.api.post<User, FormData>(`${API_BASE}/system/user/current/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  }
  
  private async getAccessToken(): Promise<string | null> {
    return localStorage.getItem(this.accessTokenKey);
  }

  async refreshAccessToken(): Promise<string> {
    const { getSessionToken } = await import('@/api/sessionToken');
    const token = await getSessionToken(true);
    if (!token) throw new Error('No active session');
    return token;
  }

  private async storeTokens(accessToken: string, refreshToken: string): Promise<void> {
    console.log("hi")
    await localStorage.setItem(this.accessTokenKey, accessToken);
    await localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  /**
   * 获取用户菜单权限
   *
   * 后端返回标准 envelope { code, msg, data: { menus: MenuItem[] }, statusCode }。
   * apiClient 用独立 axios 实例，不做 envelope 自动解包（与 axios-config.ts 的全局拦截器不共享），
   * 所以这里必须读 response.data.menus，和 fetchUserProfile / batchLogout 保持一致。
   */
  async getUserMenus(): Promise<MenuItem[]> {
    const url = `${API_BASE}/system/menu/user-menus`;
    const response = await this.api.post<ApiResponse<{ menus: MenuItem[] }>, {}>(
      url,
      {}
    );

    // Diagnostic: 后端契约 regress 时大声 fail，方便定位是 FE 读错还是 BE 返回变了
    if (!response?.data || !Array.isArray(response.data.menus)) {
      console.error('[getUserMenus] Unexpected response shape', {
        url,
        topLevelKeys: response && typeof response === 'object' ? Object.keys(response as object) : null,
        dataKeys: response?.data && typeof response.data === 'object' ? Object.keys(response.data as object) : null,
        raw: response,
      });
      throw new Error(
        `getUserMenus: expected response.data.menus to be an array, got top-level keys ${
          response && typeof response === 'object' ? JSON.stringify(Object.keys(response as object)) : String(response)
        }`,
      );
    }

    return response.data.menus;
  }

  /**
   * 批量登出（按角色）
   * 注意：标准API接口使用ApiResponse包装，需要访问response.data
   * 与commentService、BaseService的实现模式保持一致
   */
  async batchLogout(roleId: import('@/types/snowflake').SnowflakeId): Promise<BatchLogoutResponse> {
    // apiClient会自动注入JWT token
    // 对于ApiResponse<T>包装的接口，apiClient返回整个ApiResponse对象
    const response = await this.api.post<ApiResponse<BatchLogoutResponse>, { roleId: import('@/types/snowflake').SnowflakeId }>(
      `${API_BASE}/system/auth/batch-logout`,
      { roleId }
    );
    
    // 需要访问response.data获取实际数据（与commentService、BaseService保持一致）
    return response.data;
  }
}

export const authService = new AuthService(apiClient);

